import React, { useEffect } from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import GuestNavbar from '../../components/navigations/GuestNavbar';
import Footer from '../../components/landing/Footer';

const Terms = () => {
  const [activeTab, setActiveTab] = useState('terms');
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Set active tab based on current route
    if (location.pathname === '/privacy') {
      setActiveTab('privacy');
    } else {
      setActiveTab('terms');
    }
  }, [location]);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Update URL without causing a reload
    navigate(tab === 'privacy' ? '/privacy' : '/terms', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <GuestNavbar />
      
      {/* Hero Section */}
      <div className="pt-32 pb-20 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600">
        <div className="px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white sm:text-5xl">
              Provider Legal Documents
            </h1>
            <p className="mt-4 text-xl text-blue-100">
              Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-16 z-10 px-4 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex mx-auto max-w-7xl">
          <button
            onClick={() => handleTabChange('terms')}
            className={`flex-1 py-4 px-6 font-semibold transition-all duration-200 ${
              activeTab === 'terms'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Terms of Service
          </button>
          <button
            onClick={() => handleTabChange('privacy')}
            className={`flex-1 py-4 px-6 font-semibold transition-all duration-200 ${
              activeTab === 'privacy'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Privacy Policy
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-12 mx-auto max-w-8xl sm:px-6 lg:px-8">
        {activeTab === 'terms' ? (
          <TermsContent />
        ) : (
          <PrivacyContent />
        )}
      </div>

      <Footer />
    </div>
  );
};

// Terms of Service Content
const TermsContent = () => {
  const sections = [
    {
      number: "1",
      title: "Introduction",
      content: `Welcome to TripSecure. These Terms of Service govern your access to and use of the TripSecure Provider Dashboard operated by TripSecure Ghana Ltd.

By registering as a Service Provider (including travel agents, insurance brokers, hotels, transport operators, or any approved partner), you agree to comply with these Terms.

If you do not agree to these Terms, you may not access or use the Platform.`,
    },
    {
      number: "2",
      title: "Eligibility",
      content: `To use the Platform, you must:
• Be a legally registered business or authorized representative of one.
• Provide accurate, current, and complete information during registration.
• Have the legal capacity to enter into a binding agreement.

TripSecure reserves the right to approve, suspend, or terminate Provider accounts at our discretion.`,
      isList: true,
    },
    {
      number: "3",
      title: "Provider Responsibilities",
      content: `As a Provider, you agree to:
• Maintain accurate listings, pricing, and availability of your services.
• Deliver all services with professionalism, safety, and compliance with applicable laws.
• Not engage in fraudulent, misleading, or unethical practices.
• Maintain confidentiality of any data obtained through the Platform.
• Immediately update your dashboard with changes in your services, pricing, or contact details.

You are solely responsible for any disputes, claims, or liabilities arising from your services.`,
      isList: true,
    },
    {
      number: "4",
      title: "TripSecure Responsibilities",
      content: `TripSecure provides the Platform as a digital interface for Providers to manage bookings, claims, payments, and communications.

TripSecure:
• Facilitates payments and settlements as per agreed terms.
• May promote Providers' services on its digital and media platforms.
• Reserves the right to monitor, review, or remove listings that breach these Terms.

TripSecure does not guarantee bookings or service demand.`,
      isList: true,
    },
    {
      number: "5",
      title: "Payments and Fees",
      content: `• Payment schedules, commissions, and transaction fees will be outlined in your Provider Agreement.
• TripSecure reserves the right to withhold or adjust payments in cases of dispute, fraud, or service failure.
• All payments must comply with Ghanaian financial regulations and anti-money laundering laws.`,
      isList: true,
    },
    {
      number: "6",
      title: "Intellectual Property",
      content: `TripSecure retains ownership of all content, branding, and platform technology.

Providers retain ownership of their logos, content, and materials uploaded to the Platform but grant TripSecure a royalty-free license to use them for promotional purposes.`,
    },
    {
      number: "7",
      title: "Confidentiality",
      content: `Providers must maintain confidentiality of all user and company data accessed through the Platform.

You may not share or sell TripSecure data to any third party.`,
    },
    {
      number: "8",
      title: "Termination",
      content: `TripSecure may suspend or terminate your account if you:
• Violate these Terms,
• Engage in fraudulent or harmful activity, or
• Bring disrepute to the TripSecure brand.

Upon termination, all outstanding payments (subject to dispute resolution) will be settled according to the Provider Agreement.`,
      isList: true,
    },
    {
      number: "9",
      title: "Limitation of Liability",
      content: `TripSecure is not liable for:
• Loss of revenue or business opportunities,
• Service failures caused by Providers, or
• Indirect, consequential, or punitive damages.

TripSecure's total liability shall not exceed the total service fees paid to the Provider within the previous three (3) months.`,
      isList: true,
    },
    {
      number: "10",
      title: "Dispute Resolution",
      content: `Any dispute arising under these Terms shall first be resolved through negotiation.

If unresolved, it shall be referred to arbitration under the Alternative Dispute Resolution Act of Ghana.`,
    },
    {
      number: "11",
      title: "Amendments",
      content: `TripSecure may update these Terms periodically.

You will be notified of material changes via email or through the Provider Dashboard.

Continued use of the Platform indicates your acceptance of the updated Terms.`,
    },
    {
      number: "12",
      title: "Contact",
      content: `For questions about these Terms of Service:

Email: info@tripsecuregh.com`,
    },
  ];

  return (
    <div className="space-y-8">
      <div className="p-6 rounded-lg bg-white shadow-sm">
        {sections.map((section, index) => (
          <div
            key={index}
            className={`py-6 ${
              index !== sections.length - 1 ? 'border-b border-gray-200' : ''
            }`}
          >
            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 text-xl font-bold text-white bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full shadow-md">
                {section.number}
              </div>
              <div className="flex-1">
                <h3 className="mb-3 text-2xl font-bold text-gray-900">
                  {section.title}
                </h3>
                {section.isList ? (
                  <div className="space-y-2 text-gray-700 leading-relaxed whitespace-pre-line">
                    {section.content.split('\n').map((line, idx) => (
                      <p key={idx} className={line.startsWith('•') ? 'ml-4' : ''}>
                        {line}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Last Updated */}
      <div className="p-4 text-sm text-center text-gray-500 bg-gray-100 rounded-lg">
        Last updated: {new Date().toLocaleDateString()}
      </div>
    </div>
  );
};

// Privacy Policy Content
const PrivacyContent = () => {
  const sections = [
    {
      number: "1",
      title: "Information We Collect",
      content: `We may collect the following data when you use the Provider Dashboard:

• Business information (name, registration number, contact details)
• Personal information (name, phone number, email address)
• Payment and banking details
• Service listings, performance data, and analytics
• Usage data such as login times, IP addresses, and device type`,
      isList: true,
    },
    {
      number: "2",
      title: "How We Use Your Information",
      content: `TripSecure uses your information to:

• Verify and manage your provider account
• Process payments and settlements
• Facilitate bookings and claims
• Communicate with you regarding updates or issues
• Improve platform performance and user experience
• Comply with legal and regulatory obligations`,
      isList: true,
    },
    {
      number: "3",
      title: "Data Sharing",
      content: `We may share your information only with:

• Customers making bookings through TripSecure
• Financial institutions for payment processing
• Regulators and law enforcement (if required by law)
• Service partners or third-party vendors bound by confidentiality agreements

We do not sell your personal data to third parties.`,
      isList: true,
    },
    {
      number: "4",
      title: "Data Security",
      content: `TripSecure uses encryption, firewalls, and secure access protocols to protect your data.

However, no system is 100% secure. You are responsible for safeguarding your login credentials.`,
    },
    {
      number: "5",
      title: "Data Retention",
      content: `We retain Provider data as long as necessary to fulfill contractual and legal obligations.

When no longer needed, data will be securely deleted or anonymized.`,
    },
    {
      number: "6",
      title: "Your Rights",
      content: `You have the right to:

• Access and update your personal data
• Request correction or deletion
• Withdraw consent for data processing
• File a complaint with the Data Protection Commission of Ghana`,
      isList: true,
    },
    {
      number: "7",
      title: "Cookies and Tracking",
      content: `We use cookies to enhance your dashboard experience, analyze usage, and improve system performance.

You may disable cookies through your browser settings, but some features may not function properly.`,
    },
    {
      number: "8",
      title: "Updates to This Policy",
      content: `We may revise this Privacy Policy from time to time.

You will be notified of changes through the dashboard or email.`,
    },
    {
      number: "9",
      title: "Contact Us",
      content: `For privacy-related concerns or inquiries:

Email: info@tripsecuregh.com`,
    },
  ];

  return (
    <div className="space-y-8">
      <div className="p-6 rounded-lg bg-white shadow-sm">
        {sections.map((section, index) => (
          <div
            key={index}
            className={`py-6 ${
              index !== sections.length - 1 ? 'border-b border-gray-200' : ''
            }`}
          >
            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 text-xl font-bold text-white bg-gradient-to-br from-purple-600 to-pink-600 rounded-full shadow-md special-color">
                {section.number}
              </div>
              <div className="flex-1">
                <h3 className="mb-3 text-2xl font-bold text-gray-900">
                  {section.title}
                </h3>
                {section.isList ? (
                  <div className="space-y-2 text-gray-700 leading-relaxed whitespace-pre-line">
                    {section.content.split('\n').map((line, idx) => (
                      <p key={idx} className={line.startsWith('•') ? 'ml-4' : ''}>
                        {line}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Last Updated */}
      <div className="p-4 text-sm text-center text-gray-500 bg-gray-100 rounded-lg">
        Last updated: {new Date().toLocaleDateString()}
      </div>
    </div>
  );
};

export default Terms;

